import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Field, FilePicker, inputCls } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import ReviewerSelect, { useClearOffRosterPick, useIsQmReviewer, reviewerPickOf } from '../components/workflow/ReviewerSelect';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import { btnOutline } from '../components/workflow/constants';
import { profileSplitOf } from '../components/shared/EngagementFormFields';
import { screeningAnswersToFields } from '../components/shared/ScreeningFields';
import { lineFeeKey } from '../utils/serviceLines';
import {
  EngRegFormFields, emptyEngRegForm, engRegRequiredGaps, engRegFormData,
  useBizFiles, BizFileUploads, BIZFILE_DOC_TYPE,
  ScreeningUpload, screeningFileOf, SCREENING_DOC_TYPE,
  SCREENING_SECTION_HINT, SCREENING_DOC_ONLY_HINT, ENGREG_REVIEWER_HINT,
} from './WorkflowEngRegNew';
import { CHANGE_LABELS } from './WorkflowEngRegDetail';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// Engagement Registration — the edit page (docs/workflow-ui.md §4.2).
//
// The applicant's way back into a request that was returned, or into one still
// awaiting review. It is the New page's form, prefilled and numbered the same
// way, on its own page rather than inside a modal: what has to be changed is
// the whole application, and the reviewer's per-field comments read next to
// the controls they talk about instead of in a scrolling box.
//
// The two buttons are the whole point of the layout:
//   Save Changes      PATCH — the request stays where it is. From a returned
//                     request that means it does NOT go back for review yet.
//   Save & Re-submit  PATCH, then resubmit — the one action that puts a
//                     returned request back in the reviewer's queue. Nothing
//                     has to have changed for it to work: a reviewer who
//                     returned a request by mistake is a real case, and the
//                     server has always allowed it.
// A request that is still `requested` has no second button — saving already
// tells its reviewer to look again (the PATCH notifies them).

const isSupporting = (fl) => fl.docType !== BIZFILE_DOC_TYPE && fl.docType !== SCREENING_DOC_TYPE;
const screeningFilesOf = (request) => (request.files || []).filter((fl) => fl.docType === SCREENING_DOC_TYPE);

/** The request as form state — a modify re-states everything. */
const formOf = (request, requesterStaffId) => ({
  ...emptyEngRegForm(),
  groupId: request.clientGroupId || '',
  entityIds: (request.entities || []).map((e) => e.entityId).filter(Boolean),
  yearEnd: request.yearEnd || '',
  service: request.serviceProvided || '',
  serviceOther: request.serviceOther || '',
  descriptionOfService: request.descriptionOfService || '',
  ultimateParent: request.ultimateParent || '',
  overseasOperations: request.overseasOperations === true ? 'yes' : request.overseasOperations === false ? 'no' : '',
  consolidatedFs: request.consolidatedFs || '',
  isPie: request.isPie === true ? 'yes' : request.isPie === false ? 'no' : '',
  pieType: request.isPie === true ? (request.pieType || '') : '',
  pieTypeOther: request.isPie === true ? (request.pieTypeOther || '') : '',
  proposedFees: request.proposedFees != null ? String(request.proposedFees) : '',
  proposedFeesCcy: request.proposedFeesCcy || 'SGD',
  proposedFeesSplit: {
    ...Object.fromEntries((request.entities || [])
      .filter((e) => e.entityId && e.proposedFees !== null && e.proposedFees !== undefined)
      .map((e) => [e.entityId, String(e.proposedFees)])),
    ...Object.fromEntries((request.serviceLines || [])
      .filter((l) => l.proposedFees !== null && l.proposedFees !== undefined)
      .map((l) => [lineFeeKey(String(l.id)), String(l.proposedFees)])),
  },
  // The additional services, keyed by their row id (new lines get a fresh key).
  serviceLines: (request.serviceLines || []).filter((l) => l.entityId).map((l) => ({
    k: String(l.id), entityId: l.entityId, service: l.serviceProvided || '', serviceOther: l.serviceOther || '',
    descriptionOfService: l.descriptionOfService || '',
  })),
  profileSplit: profileSplitOf(request.entities),
  ssicPrimary: request.ssicPrimary || '',
  ssicSecondary: request.ssicSecondary || '',
  principalActivity1: request.principalActivity1 || '',
  principalActivity2: request.principalActivity2 || '',
  secRegistered: request.secRegistered === true ? 'yes' : request.secRegistered === false ? 'no' : '',
  partnerStaffIds: (request.partners || []).map((m) => m.staffId).filter(Boolean),
  directorStaffIds: (request.directors || []).map((m) => m.staffId).filter(Boolean),
  managerStaffIds: (request.managers || []).map((m) => m.staffId).filter(Boolean),
  // The requester is always on the team (re-added on save) — shown locked
  // even on an older request that predates the rule.
  teamStaffIds: [...new Set([
    ...(requesterStaffId != null ? [requesterStaffId] : []),
    ...(request.teamMembers || []).map((m) => m.staffId).filter(Boolean),
  ])],
  // Chips of people no longer in a field's list still read by name. Not posted.
  knownStaff: [...(request.partners || []), ...(request.directors || []), ...(request.managers || []), ...(request.teamMembers || [])],
  isGroupAudit: request.isGroupAudit === true ? 'yes' : request.isGroupAudit === false ? 'no' : '',
  underInvestigation: request.underInvestigation === true ? 'yes' : request.underInvestigation === false ? 'no' : '',
  tcwgFeeCommunicated: request.tcwgFeeCommunicated === true ? 'yes' : request.tcwgFeeCommunicated === false ? 'no' : '',
  screeningRequired: request.screeningRequired === true ? 'yes' : request.screeningRequired === false ? 'no' : '',
  ...screeningAnswersToFields(request),
  independenceDeclared: true,
  remarks: request.remarks || '',
  reviewerStaffId: reviewerPickOf(request),
});

const EngRegEditForm = ({ request, events, requesterStaffId, onSaved, initialNotice = null }) => {
  const navigate = useNavigate();
  const backTo = `/workflow/engreg/${request.id}`;
  const returned = request.status === 'awaiting_info';

  const [options, setOptions] = useState(null);
  const [f, setF] = useState(() => formOf(request, requesterStaffId));
  const [files, setFiles] = useState([]);
  const [bizFiles, setBizFiles] = useBizFiles(f.entityIds);
  const [screeningFile, setScreeningFile] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(initialNotice);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const setReviewer = useCallback((v) => setF((prev) => ({ ...prev, reviewerStaffId: v })), []);
  // A named reviewer no longer on the roster is cleared and must be re-picked.
  useClearOffRosterPick(f.reviewerStaffId, setReviewer, null, 'master');
  const { removeIds, toggle: toggleRemove, appendTo } = useRemovableFiles();
  // BizFiles are replaced by uploading a new one, never removed on their own —
  // a registration without one cannot be saved, so a Remove button would only
  // lead to a guaranteed 400 (contract §3.2). Only supporting documents are
  // removable here.
  const supportingFiles = (request.files || []).filter(isSupporting);

  // The reviewer's per-field comments, read-only here: whoever is editing is
  // the applicant, so this page never writes a comment, only shows them.
  const fc = useFieldComments({ events, canComment: false, returned, labels: CHANGE_LABELS });

  useEffect(() => {
    (async () => {
      try { setOptions(await fetchJSON('/api/workflow/engreg/options')); }
      catch (e) { setError(e.message); }
    })();
  }, []);

  // Everything still empty, keyed to the control it belongs to — after the
  // first save the page marks those controls and lists them in the banner.
  // A company's BizFile already on the request counts as attached.
  // The screening question is asked only with a reviewer from the QM department.
  const screeningAsked = useIsQmReviewer(f.reviewerStaffId);
  const gaps = useMemo(() => engRegRequiredGaps(f, {
    bizFiles, existing: request.files || [], entities: (options || {}).entities || [],
    multiFyeServices: (options || {}).multiFyeServices || [],
    requesterStaffId,
    screeningAsked,
  }), [f, bizFiles, request.files, options, requesterStaffId, screeningAsked]);
  const validation = useFormValidation(gaps);

  /** PATCH the request. Returns true when it went through. */
  const save = async () => {
    if (!validation.validate()) { setError(null); setNotice(null); return false; }
    // Companies newly added here get the same live guard as the New page — the
    // ones this request already covers are exempt (the server exempts them too).
    if (duplicates.length > 0) { setError(duplicates[0].message); setNotice(null); return false; }
    // Flipping the answer to Yes drops both a freshly picked file and the one
    // already on the request (the server hard-deletes the stored copy) —
    // sending it would only earn a 400.
    const form = engRegFormData(f, files, bizFiles, !screeningAsked || f.screeningRequired === 'no' ? screeningFile : null, screeningAsked);
    appendTo(form);
    await fetchJSON(`/api/workflow/engreg/${request.id}`, { method: 'PATCH', body: form });
    return true;
  };

  // Save only: the request stays where it is and the page stays open, showing
  // what was just stored (onSaved remounts this form from the reloaded
  // request, which is also what clears the file pickers).
  const saveOnly = async () => {
    setSaving(true); setError(null); setNotice(null);
    try {
      if (!(await save())) { setSaving(false); return; }
      await onSaved(returned
        ? 'Changes saved. They have not been re-submitted yet — press Save & Re-submit when you are ready.'
        : 'Changes saved. The reviewer has been notified to look again.');
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  // Save and send back for review. A failed re-submit (the coverage was taken
  // while the request sat returned, the named reviewer left the roster) leaves
  // the saved changes in place and says why — nothing is lost.
  const saveAndResubmit = async () => {
    setSaving(true); setError(null); setNotice(null);
    try {
      if (!(await save())) { setSaving(false); return; }
      await fetchJSON(`/api/workflow/engreg/${request.id}/resubmit`, { method: 'POST' });
      navigate(backTo, { state: { notice: 'Re-submitted — the reviewer has been notified.' } });
      return;
    } catch (e) {
      setError(e.message);
      await onSaved(null, { silent: true });
    }
    setSaving(false);
  };

  return (
    <FormShell
      backTo={backTo}
      backLabel={request.requestNo}
      title={`Modify ${request.requestNo}`}
      subtitle={returned
        ? 'Update what the reviewer asked for, then send it back for review.'
        : 'This request is still awaiting review. Saving notifies the reviewer to look at it again.'}
      error={error || validation.error}
      notice={notice}
      onSubmit={returned ? saveAndResubmit : saveOnly}
      submitLabel={returned ? 'Save & Re-submit' : 'Save Changes'}
      submittingLabel="Saving…"
      submitting={saving}
      actionsExtra={returned ? (
        <button type="button" onClick={saveOnly} disabled={saving} className={btnOutline}>Save Changes</button>
      ) : null}
    >
      <FieldCommentsProvider value={fc}>
        {/* Only while the request is actually sitting in awaiting_info: once it
            is back in the queue the reason is history, and History is where it
            belongs — the same rule the status card follows. */}
        {returned && <ReturnCommentsBanner events={events} />}
        <FormValidationProvider value={validation.providerValue}>
          <EngRegFormFields f={f} set={set} options={options} requestId={request.id} onDuplicates={setDuplicates}
            requesterStaffId={requesterStaffId} />

          <FormSection step={4} title="BizFile">
            <BizFileUploads
              f={f}
              options={options}
              bizFiles={bizFiles}
              setBizFiles={setBizFiles}
              existing={request.files || []}
            />
          </FormSection>

          <FormSection step={5} title="Attachments">
            <EditableAttachments
              files={supportingFiles}
              removeIds={removeIds}
              onToggle={toggleRemove}
              label="Current Supporting Files"
              downloadHref={(fl) => `${API_BASE_URL}/api/workflow/engreg/${request.id}/files/${fl.id}`}
            />
            <FieldCommentInline field="files" />
            <Field label="Add More Supporting Files" hint="PDF / DOC / DOCX / XLS / XLSX / JPG / PNG, up to 30 MB per file.">
              <FilePicker multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                files={files} />
            </Field>
          </FormSection>

          {/* No independence declaration here: it was given when the request
              was filed and stands for the request, not for each edit. */}
          <FormSection step={6} title="Remarks">
            <Field label="Remarks" field="remarks">
              <textarea rows="2" value={f.remarks} onChange={(e) => set('remarks', e.target.value)} className={inputCls} />
            </Field>
          </FormSection>

          <FormSection step={7} title="Reviewer">
            <Field label="Reviewer" required field="reviewerName" hint={ENGREG_REVIEWER_HINT}>
              <ReviewerSelect roster="master" value={f.reviewerStaffId} onChange={setReviewer} />
            </Field>
          </FormSection>

          {/* Same order as the New page: Screening comes after the Reviewer; the
              question only with a reviewer from QM, otherwise just the upload.
              The current screening document is removable: unlike the BizFile it
              is optional, so "remove and save nothing" is a legitimate end state. */}
          {f.reviewerStaffId && (
            <FormSection step={8} title="Screening" hint={screeningAsked ? SCREENING_SECTION_HINT : SCREENING_DOC_ONLY_HINT}>
              <ScreeningUpload
                f={f}
                set={set}
                screeningFile={screeningFile}
                setScreeningFile={setScreeningFile}
                existing={request.files || []}
                docOnly={!screeningAsked}
                currentBlock={screeningFileOf(request.files) ? (
                  screeningAsked && f.screeningRequired === 'yes' ? (
                    <p className="text-sm text-amber-700">
                      Switching to Yes removes the attached screening document — QM performs the screening after approval.
                    </p>
                  ) : (
                    <EditableAttachments
                      files={screeningFilesOf(request)}
                      removeIds={removeIds}
                      onToggle={toggleRemove}
                      label="Current Screening Document"
                      downloadHref={(fl) => `${API_BASE_URL}/api/workflow/engreg/${request.id}/files/${fl.id}`}
                    />
                  )
                ) : null}
              />
            </FormSection>
          )}
        </FormValidationProvider>
      </FieldCommentsProvider>
    </FormShell>
  );
};

const WorkflowEngRegEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [events, setEvents] = useState([]);
  const [requesterStaffId, setRequesterStaffId] = useState(null);
  const [canModify, setCanModify] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // A save reloads the request and remounts the form on the stored version —
  // that is what clears the file pickers and the "delete this attachment" ticks.
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    const [detail, hist] = await Promise.all([
      fetchJSON(`/api/workflow/engreg/${id}`),
      fetchJSON(`/api/workflow/engreg/${id}/events`),
    ]);
    setRequest(detail.request);
    setCanModify(detail.canModify);
    setRequesterStaffId(detail.requesterStaffId ?? null);
    setEvents(hist.data || []);
  }, [id]);

  useEffect(() => { load().catch((e) => setError(e.message)); }, [load]);

  const onSaved = useCallback(async (msg, { silent = false } = {}) => {
    await load();
    setVersion((v) => v + 1);
    if (!silent) setNotice(msg);
  }, [load]);

  if (error) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      </div>
    );
  }
  if (!request || canModify === null) {
    return <div className="p-4 sm:p-6 max-w-3xl mx-auto text-sm text-slate-500">Loading…</div>;
  }
  // The detail page does not offer Modify when the server says no, so this is
  // reached by typing the URL. Send them to the request itself.
  if (!canModify) {
    navigate(`/workflow/engreg/${id}`, { replace: true });
    return null;
  }

  return (
    <EngRegEditForm
      key={version}
      request={request}
      events={events}
      requesterStaffId={requesterStaffId}
      onSaved={onSaved}
      initialNotice={notice}
    />
  );
};

export default WorkflowEngRegEdit;
